import { ErrorHandler, Injectable } from '@angular/core';

/** Wording differs per browser/bundler, so match on a set of known messages. */
const STALE_BUILD_MESSAGES = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'unable to preload css',
  'chunkloaderror',
  'loading chunk',
];

const RELOAD_MARKER = 'kavach.staleBuildReloadAt';
const RELOAD_COOLDOWN_MS = 30_000;

function messageOf(error: any): string {
  if (!error) return '';
  // Angular/zone wrap the original error in `rejection` or `error` often
  const parts = [error.message, error.rejection?.message, error.error?.message, typeof error === 'string' ? error : ''];
  return parts.filter(Boolean).join(' ').toLowerCase();
}

export function isStaleBuildError(error: unknown): boolean {
  const message = messageOf(error);
  return !!message && STALE_BUILD_MESSAGES.some((msg) => message.includes(msg));
}

/** A failed read must never block the reload. */
function lastReloadAge(): number {
  try {
    const at = Number(sessionStorage.getItem(RELOAD_MARKER));
    return at ? Date.now() - at : Number.POSITIVE_INFINITY;
  } catch {
    return Number.POSITIVE_INFINITY;
  }
}

function markReload(): void {
  try {
    sessionStorage.setItem(RELOAD_MARKER, String(Date.now()));
  } catch {
    /* ignore - worst case we reload one extra time */
  }
}

/**
 * @returns true when the error was a stale-build error we are recovering from,
 *          i.e. the caller should stop handling it.
 */
export function recoverFromStaleBuild(error: unknown): boolean {
  if (!isStaleBuildError(error)) return false;

  if (lastReloadAge() < RELOAD_COOLDOWN_MS) {
    // We already reloaded and the chunk is still missing - reloading again would loop.
    console.error('Chunk still missing after reloading for a new build.', error);
    return false;
  }

  console.warn('A new version of the application was deployed; reloading to pick up the new bundles.');
  markReload();
  window.location.reload();
  return true;
}

/** Router/component errors reach the app through ErrorHandler. */
@Injectable()
export class StaleBuildErrorHandler implements ErrorHandler {
  private readonly defaultHandler = new ErrorHandler();

  handleError(error: unknown): void {
    if (recoverFromStaleBuild(error)) return;
    /** Anything that is not a stale chunk keeps Angular's default flow */
    this.defaultHandler.handleError(error);
  }
}
